import { useCallback, useEffect, useMemo, useState } from "react";
import PortalLayout from "../../components/PortalLayout";
import { api, formatApiErrorDetail, formatDate } from "../../lib/api";

const STATUS_OPTIONS = [
  { value: "draft", label: "Bozza" },
  { value: "published", label: "Pubblicato" },
  { value: "closed", label: "Chiuso" },
  { value: "archived", label: "Archiviato" },
];

const EMPTY_EVENT = {
  title: "",
  category: "private_dinner",
  description: "",
  starts_at: "",
  ends_at: "",
  location_city: "",
  location_venue: "",
  location_address: "",
  dress_code: "",
  capacity: "",
  cover_image_url: "",
  status: "draft",
};

export default function AdminEventsPage() {
  const [events, setEvents] = useState([]);
  const [categories, setCategories] = useState([]);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(null);
  const [rsvpFor, setRsvpFor] = useState(null);

  const load = useCallback(async () => {
    try {
      const [evRes, catRes] = await Promise.all([
        api.get("/admin/events"),
        api.get("/admin/events/categories"),
      ]);
      setEvents(evRes.data);
      setCategories(catRes.data);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const remove = async (ev) => {
    if (!window.confirm(`Eliminare definitivamente "${ev.title}"? Verranno cancellate anche tutte le RSVPs collegate.`)) return;
    try {
      await api.delete(`/admin/events/${ev.id}`);
      await load();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const publish = async (ev) => {
    if (!window.confirm(`Pubblicare "${ev.title}"? Verranno inviate email di invito a tutti i clienti con KYC completo.`)) return;
    try {
      await api.post(`/admin/events/${ev.id}/publish`);
      await load();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <PortalLayout>
      <section
        className="max-w-[1280px] mx-auto px-6 sm:px-8 pt-14 pb-8"
        data-testid="admin-events-page"
      >
        <div className="flex items-baseline justify-between flex-wrap gap-4 br-fade-up">
          <div>
            <div className="br-overline">Calendario eventi</div>
            <h1 className="font-serif-display text-[clamp(2.2rem,4vw,3.2rem)] mt-3">
              Eventi.
            </h1>
            <p className="text-[0.95rem] text-[color:var(--br-text-dim)] mt-3 max-w-[640px]">
              Crea inviti riservati, pubblicali ai clienti con KYC completo e gestisci le richieste
              di partecipazione (accept/reject con commento).
            </p>
          </div>
          <button
            onClick={() => setEditing({ ...EMPTY_EVENT, _isNew: true })}
            className="br-btn-primary"
            data-testid="event-create-btn"
          >
            + Nuovo evento
          </button>
        </div>

        {error && (
          <div
            className="mt-6 br-card p-4 text-[color:var(--br-red)]"
            data-testid="events-error"
          >
            {String(error)}
          </div>
        )}

        <div
          className="mt-8 br-card overflow-x-auto br-fade-up br-delay-1"
          data-testid="admin-events-table"
        >
          <table className="w-full text-left">
            <thead>
              <tr style={{ borderBottom: "1px solid var(--br-line)" }}>
                <Th>Titolo</Th>
                <Th>Categoria</Th>
                <Th>Quando</Th>
                <Th>Stato</Th>
                <Th>RSVP</Th>
                <Th align="right">Azioni</Th>
              </tr>
            </thead>
            <tbody>
              {events.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-5 py-12 text-center text-[color:var(--br-text-dim)] italic">
                    Nessun evento ancora creato.
                  </td>
                </tr>
              )}
              {events.map((ev) => (
                <tr
                  key={ev.id}
                  className="border-b"
                  style={{ borderColor: "var(--br-line)" }}
                  data-testid={`event-row-${ev.id}`}
                >
                  <Td>
                    <div className="text-[color:var(--br-parchment)]">{ev.title}</div>
                    {ev.location_venue && (
                      <div className="font-mono-fin text-[0.72rem] text-[color:var(--br-text-dim)]">
                        {ev.location_venue}{ev.location_city ? ` · ${ev.location_city}` : ""}
                      </div>
                    )}
                  </Td>
                  <Td className="font-mono-fin text-[0.78rem]" style={{ color: "var(--br-bronze)" }}>
                    {ev.category_label}
                  </Td>
                  <Td className="font-mono-fin text-[0.8rem]">{ev.starts_at ? formatDate(ev.starts_at) : "—"}</Td>
                  <Td>
                    <StatusBadge status={ev.status} />
                  </Td>
                  <Td className="font-mono-fin text-[0.78rem]">
                    <span style={{ color: "var(--br-bronze)" }}>{ev.rsvp_counts.pending}</span>
                    <span style={{ color: "var(--br-text-dim)" }}> / </span>
                    <span style={{ color: "var(--br-bronze-soft)" }}>{ev.rsvp_counts.accepted}</span>
                    <span style={{ color: "var(--br-text-dim)" }}> / </span>
                    <span style={{ color: "var(--br-red)" }}>{ev.rsvp_counts.declined}</span>
                  </Td>
                  <Td align="right">
                    <div className="flex items-center justify-end gap-2 flex-wrap">
                      <button
                        onClick={() => setRsvpFor(ev)}
                        className="br-btn-outline"
                        style={{ padding: "6px 12px", fontSize: "0.62rem" }}
                        data-testid={`event-rsvps-${ev.id}`}
                      >
                        RSVPs
                      </button>
                      {ev.status === "draft" && (
                        <button
                          onClick={() => publish(ev)}
                          className="br-btn-outline"
                          style={{
                            padding: "6px 12px",
                            fontSize: "0.62rem",
                            borderColor: "var(--br-bronze-soft)",
                            color: "var(--br-bronze-soft)",
                          }}
                          data-testid={`event-publish-${ev.id}`}
                        >
                          Pubblica
                        </button>
                      )}
                      <button
                        onClick={() => setEditing({ ...ev, _toForm: true })}
                        className="br-btn-outline"
                        style={{ padding: "6px 12px", fontSize: "0.62rem" }}
                        data-testid={`event-edit-${ev.id}`}
                      >
                        Modifica
                      </button>
                      <button
                        onClick={() => remove(ev)}
                        className="br-btn-outline"
                        style={{
                          padding: "6px 12px",
                          fontSize: "0.62rem",
                          borderColor: "var(--br-red)",
                          color: "var(--br-red)",
                        }}
                        data-testid={`event-delete-${ev.id}`}
                      >
                        Elimina
                      </button>
                    </div>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {editing && (
        <EventEditModal
          event={editing}
          categories={categories}
          onCancel={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            load();
          }}
          onError={(msg) => setError(msg)}
        />
      )}

      {rsvpFor && (
        <RsvpsListModal
          event={rsvpFor}
          onClose={() => {
            setRsvpFor(null);
            load();
          }}
        />
      )}
    </PortalLayout>
  );
}

function StatusBadge({ status }) {
  const map = {
    draft: { color: "var(--br-text-dim)", label: "Bozza" },
    published: { color: "var(--br-bronze-soft)", label: "Pubblicato" },
    closed: { color: "var(--br-text-dim)", label: "Chiuso" },
    archived: { color: "var(--br-text-dim)", label: "Archiviato" },
  };
  const s = map[status] || map.draft;
  return (
    <span
      className="font-mono-fin text-[0.6rem] tracking-[0.22em] uppercase border px-2 py-0.5"
      style={{ color: s.color, borderColor: s.color }}
    >
      {s.label}
    </span>
  );
}

function toLocalDtInput(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "";
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch {
    return "";
  }
}

function fromLocalDtInput(local) {
  if (!local) return null;
  const d = new Date(local);
  if (isNaN(d.getTime())) return null;
  return d.toISOString();
}

function EventEditModal({ event, categories, onCancel, onSaved, onError }) {
  const isNew = Boolean(event._isNew);
  const [form, setForm] = useState({
    ...event,
    starts_at: event._toForm ? toLocalDtInput(event.starts_at) : event.starts_at || "",
    ends_at: event._toForm ? toLocalDtInput(event.ends_at) : event.ends_at || "",
    capacity: event.capacity ?? "",
  });
  const [busy, setBusy] = useState(false);

  const setField = (k) => (e) => {
    const v = e?.target?.type === "checkbox" ? e.target.checked : e?.target?.value;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const submit = async () => {
    setBusy(true);
    try {
      const starts_iso = fromLocalDtInput(form.starts_at);
      const ends_iso = fromLocalDtInput(form.ends_at);
      if (!starts_iso) {
        onError("La data di inizio è obbligatoria");
        setBusy(false);
        return;
      }
      const payload = {
        title: String(form.title || "").trim(),
        category: form.category,
        description: String(form.description || "").trim() || null,
        starts_at: starts_iso,
        ends_at: ends_iso,
        location_city: String(form.location_city || "").trim() || null,
        location_venue: String(form.location_venue || "").trim() || null,
        location_address: String(form.location_address || "").trim() || null,
        dress_code: String(form.dress_code || "").trim() || null,
        capacity: form.capacity ? Number(form.capacity) : null,
        cover_image_url: String(form.cover_image_url || "").trim() || null,
        status: form.status,
      };
      if (!payload.title) {
        onError("Il titolo dell'evento è obbligatorio.");
        setBusy(false);
        return;
      }
      if (isNew) {
        await api.post("/admin/events", payload);
      } else {
        await api.patch(`/admin/events/${event.id}`, payload);
      }
      onSaved();
    } catch (e) {
      onError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-6 py-12 overflow-y-auto"
      style={{ background: "rgba(10,14,26,0.78)", backdropFilter: "blur(8px)" }}
      onClick={onCancel}
      data-testid="event-edit-modal"
    >
      <div
        className="br-card br-aurora-ribbon relative max-w-[760px] w-full px-9 py-10 br-fade-up"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="br-overline">{isNew ? "Nuovo evento" : "Modifica evento"}</div>
        <h2 className="font-serif-display text-[1.9rem] mt-3 leading-[1.1]">
          {form.title || "—"}
        </h2>

        <div className="mt-7 grid sm:grid-cols-2 gap-4">
          <Inp label="Titolo" value={form.title} onChange={setField("title")} colSpan testid="event-field-title" />
          <SelectField
            label="Categoria"
            value={form.category}
            onChange={setField("category")}
            options={categories.map((c) => ({ value: c.key, label: c.label.it }))}
            testid="event-field-category"
          />
          <SelectField
            label="Stato"
            value={form.status}
            onChange={setField("status")}
            options={STATUS_OPTIONS}
            testid="event-field-status"
          />
          <Inp label="Inizio (locale)" type="datetime-local" value={form.starts_at} onChange={setField("starts_at")} testid="event-field-starts" />
          <Inp label="Fine (locale, opzionale)" type="datetime-local" value={form.ends_at} onChange={setField("ends_at")} testid="event-field-ends" />
          <Inp label="Città" value={form.location_city} onChange={setField("location_city")} testid="event-field-city" />
          <Inp label="Venue" value={form.location_venue} onChange={setField("location_venue")} testid="event-field-venue" />
          <Inp label="Indirizzo" value={form.location_address} onChange={setField("location_address")} colSpan testid="event-field-address" />
          <Inp label="Dress code" value={form.dress_code} onChange={setField("dress_code")} testid="event-field-dress" />
          <Inp label="Posti" type="number" value={form.capacity} onChange={setField("capacity")} mono testid="event-field-capacity" />
          <Inp label="Cover image URL (opzionale)" value={form.cover_image_url} onChange={setField("cover_image_url")} colSpan testid="event-field-cover" />
          <label className="block sm:col-span-2">
            <span className="br-overline">Descrizione</span>
            <textarea
              value={form.description || ""}
              onChange={setField("description")}
              rows={4}
              className="mt-2 w-full bg-transparent border px-3 py-2 text-[0.95rem] text-[color:var(--br-parchment)] outline-none focus:border-[color:var(--br-bronze)] resize-none"
              style={{ borderColor: "var(--br-line-bright)" }}
              data-testid="event-field-description"
            />
          </label>
        </div>

        <div className="mt-8 grid grid-cols-2 gap-3">
          <button
            onClick={onCancel}
            disabled={busy}
            className="br-btn-outline"
            data-testid="event-cancel"
          >
            Annulla
          </button>
          <button
            onClick={submit}
            disabled={busy}
            className="br-btn-primary"
            data-testid="event-save"
          >
            {busy ? "Salvataggio…" : isNew ? "Crea evento" : "Salva modifiche"}
          </button>
        </div>
      </div>
    </div>
  );
}

function RsvpsListModal({ event, onClose }) {
  const [rsvps, setRsvps] = useState([]);
  const [error, setError] = useState("");
  const [decision, setDecision] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/admin/events/${event.id}/rsvps`);
      setRsvps(data);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }, [event.id]);

  useEffect(() => {
    load();
  }, [load]);

  const counts = useMemo(() => {
    const c = { pending: 0, accepted: 0, declined: 0 };
    rsvps.forEach((r) => {
      if (c[r.status] !== undefined) c[r.status]++;
    });
    return c;
  }, [rsvps]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-6 py-12 overflow-y-auto"
      style={{ background: "rgba(10,14,26,0.78)", backdropFilter: "blur(8px)" }}
      onClick={onClose}
      data-testid="rsvps-list-modal"
    >
      <div
        className="br-card br-aurora-ribbon relative max-w-[900px] w-full px-9 py-10 br-fade-up"
        onClick={(e) => e.stopPropagation()}
        style={{ maxHeight: "85vh", overflowY: "auto" }}
      >
        <div className="br-overline">Gestione RSVPs</div>
        <h2 className="font-serif-display text-[1.9rem] mt-3 leading-[1.1]">{event.title}</h2>
        <div className="mt-2 font-mono-fin text-[0.78rem] text-[color:var(--br-text-dim)]">
          {event.category_label} · {formatDate(event.starts_at)}
        </div>
        <div className="mt-2 font-mono-fin text-[0.78rem]">
          <span style={{ color: "var(--br-bronze)" }}>pending: {counts.pending}</span>
          <span style={{ color: "var(--br-text-dim)" }}> · </span>
          <span style={{ color: "var(--br-bronze-soft)" }}>accepted: {counts.accepted}</span>
          <span style={{ color: "var(--br-text-dim)" }}> · </span>
          <span style={{ color: "var(--br-red)" }}>declined: {counts.declined}</span>
        </div>

        {error && (
          <div className="mt-4 br-card p-3 text-[color:var(--br-red)]">{String(error)}</div>
        )}

        <div className="mt-6 space-y-2">
          {rsvps.length === 0 && (
            <div
              className="br-card p-5 italic text-center"
              style={{ color: "var(--br-text-dim)" }}
            >
              Nessuna richiesta per questo evento.
            </div>
          )}
          {rsvps.map((r) => (
            <div
              key={r.id}
              className="br-card p-4 flex flex-wrap gap-4 items-start justify-between"
              data-testid={`rsvp-row-${r.id}`}
            >
              <div className="flex-1 min-w-[220px]">
                <div className="text-[color:var(--br-parchment)] font-medium">{r.user.name}</div>
                <div className="font-mono-fin text-[0.72rem] text-[color:var(--br-text-dim)]">
                  {r.user.email} · {r.user.locale.toUpperCase()}
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-2 text-[0.78rem]">
                  <StatusBadge status={r.status === "pending" ? "draft" : r.status === "accepted" ? "published" : "archived"} />
                  {r.plus_one && (
                    <span
                      className="font-mono-fin text-[0.6rem] tracking-[0.22em] uppercase border px-2 py-0.5"
                      style={{ color: "var(--br-bronze)", borderColor: "var(--br-bronze)" }}
                    >
                      +1
                    </span>
                  )}
                </div>
                {r.notes && (
                  <p className="text-[0.85rem] text-[color:var(--br-text-dim)] italic mt-2">
                    {`\u201C${r.notes}\u201D`}
                  </p>
                )}
                {r.admin_comment && (
                  <p
                    className="text-[0.82rem] mt-2 px-3 py-2 border-l-2"
                    style={{
                      borderColor: r.status === "declined" ? "var(--br-red)" : "var(--br-bronze)",
                      background: "rgba(167,128,73,0.04)",
                      color: "var(--br-parchment)",
                    }}
                  >
                    <span className="font-mono-fin text-[0.62rem] tracking-[0.18em] uppercase mr-2" style={{ color: "var(--br-text-dim)" }}>
                      tua nota
                    </span>
                    {r.admin_comment}
                  </p>
                )}
              </div>
              {r.status === "pending" && (
                <div className="flex gap-2">
                  <button
                    onClick={() => setDecision({ rsvp: r, decision: "accepted" })}
                    className="br-btn-outline"
                    style={{
                      padding: "6px 12px",
                      fontSize: "0.62rem",
                      borderColor: "var(--br-bronze-soft)",
                      color: "var(--br-bronze-soft)",
                    }}
                    data-testid={`rsvp-accept-${r.id}`}
                  >
                    Accetta
                  </button>
                  <button
                    onClick={() => setDecision({ rsvp: r, decision: "declined" })}
                    className="br-btn-outline"
                    style={{
                      padding: "6px 12px",
                      fontSize: "0.62rem",
                      borderColor: "var(--br-red)",
                      color: "var(--br-red)",
                    }}
                    data-testid={`rsvp-decline-${r.id}`}
                  >
                    Rifiuta
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>

        <button onClick={onClose} className="br-btn-outline w-full mt-8" data-testid="rsvps-close">
          Chiudi
        </button>
      </div>

      {decision && (
        <RsvpDecisionModal
          info={decision}
          onCancel={() => setDecision(null)}
          onDone={() => {
            setDecision(null);
            load();
          }}
          onError={setError}
        />
      )}
    </div>
  );
}

function RsvpDecisionModal({ info, onCancel, onDone, onError }) {
  const { rsvp, decision } = info;
  const isAccept = decision === "accepted";
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!isAccept && !comment.trim()) {
      onError("Il commento è obbligatorio quando rifiuti una richiesta.");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/admin/rsvps/${rsvp.id}/decision`, {
        decision,
        comment: comment.trim() || null,
      });
      onDone();
    } catch (e) {
      onError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-6 py-12"
      style={{ background: "rgba(10,14,26,0.85)", backdropFilter: "blur(10px)" }}
      onClick={onCancel}
      data-testid="rsvp-decision-modal"
    >
      <div
        className="br-card relative max-w-[560px] w-full px-8 py-9 br-fade-up"
        onClick={(e) => e.stopPropagation()}
        style={
          isAccept
            ? { borderTop: "3px solid var(--br-bronze-soft)" }
            : { borderTop: "3px solid var(--br-red)" }
        }
      >
        <div
          className="br-overline"
          style={{ color: isAccept ? "var(--br-bronze-soft)" : "var(--br-red)" }}
          data-testid="rsvp-decision-action"
        >
          {isAccept ? "Accettazione" : "Rifiuto"}
        </div>
        <h3 className="font-serif-display text-[1.7rem] mt-3 leading-[1.1]">
          {rsvp.user.name}
        </h3>
        <div className="font-mono-fin text-[0.74rem] text-[color:var(--br-text-dim)] mt-1">
          {rsvp.user.email} · {rsvp.user.locale.toUpperCase()}
        </div>
        <p className="mt-5 text-[0.92rem] text-[color:var(--br-text)] leading-relaxed">
          {isAccept
            ? "Il cliente riceverà conferma via email firmata Events Team con i dettagli logistici."
            : "Il cliente riceverà email con la motivazione. Il commento è obbligatorio."}
        </p>

        <label className="block mt-5">
          <span className="br-overline">
            {isAccept ? "Nota opzionale" : "Motivo del rifiuto"} ·{" "}
            <span style={{ color: "var(--br-text-dim)" }}>
              locale {rsvp.user.locale.toUpperCase()}
            </span>
          </span>
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            rows={4}
            placeholder={isAccept ? "Es. Posto già confermato in prima fila." : "Es. Posti contingentati questa edizione, segnaliamo i prossimi inviti."}
            className="mt-2 w-full bg-transparent border px-3 py-2 text-[0.95rem] text-[color:var(--br-parchment)] outline-none focus:border-[color:var(--br-bronze)] resize-none"
            style={{ borderColor: "var(--br-line-bright)" }}
            data-testid="rsvp-decision-comment"
          />
        </label>

        <div className="mt-7 grid grid-cols-2 gap-3">
          <button
            onClick={onCancel}
            disabled={busy}
            className="br-btn-outline"
            data-testid="rsvp-decision-cancel"
          >
            Annulla
          </button>
          <button
            onClick={submit}
            disabled={busy}
            className={isAccept ? "br-btn-primary" : "br-btn-outline"}
            style={
              !isAccept
                ? { borderColor: "var(--br-red)", color: "var(--br-red)" }
                : undefined
            }
            data-testid="rsvp-decision-submit"
          >
            {busy ? "Invio…" : isAccept ? "Accetta e notifica" : "Rifiuta e notifica"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Inp({ label, value, onChange, type = "text", mono = false, colSpan = false, testid }) {
  return (
    <label className={`block ${colSpan ? "sm:col-span-2" : ""}`}>
      <span className="br-overline">{label}</span>
      <input
        type={type}
        value={value ?? ""}
        onChange={onChange}
        className={`mt-2 w-full bg-transparent border px-3 py-2 text-[0.95rem] text-[color:var(--br-parchment)] outline-none focus:border-[color:var(--br-bronze)] ${
          mono ? "font-mono-fin" : ""
        }`}
        style={{ borderColor: "var(--br-line-bright)" }}
        data-testid={testid}
      />
    </label>
  );
}

function SelectField({ label, value, onChange, options, testid }) {
  return (
    <label className="block">
      <span className="br-overline">{label}</span>
      <select
        value={value}
        onChange={onChange}
        className="mt-2 w-full bg-transparent border px-3 py-2 text-[0.95rem] text-[color:var(--br-parchment)] outline-none focus:border-[color:var(--br-bronze)]"
        style={{ borderColor: "var(--br-line-bright)" }}
        data-testid={testid}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value} style={{ background: "var(--br-midnight)" }}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}

function Th({ children, align = "left" }) {
  return (
    <th
      className="px-4 py-3 font-mono-fin text-[0.6rem] tracking-[0.22em] uppercase font-medium"
      style={{ color: "var(--br-text-dim)", textAlign: align }}
    >
      {children}
    </th>
  );
}

function Td({ children, align = "left", className = "", style }) {
  return (
    <td
      className={`px-4 py-3 text-[0.88rem] ${className}`}
      style={{ textAlign: align, ...style }}
    >
      {children}
    </td>
  );
}
